import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Flame, Coins, Bitcoin } from "lucide-react";
import client, { fmtUsd, fmtPct, fmtNum } from "../lib/api";
import Sparkline from "../components/Sparkline";

const StatTile = ({ label, value, delta }) => (
  <div className="glass p-6 flex flex-col gap-1">
    <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
    <div className="font-display text-2xl lg:text-3xl font-mono-num">{value}</div>
    {delta !== undefined && delta !== null && (
      <div className={`text-sm font-mono-num ${delta >= 0 ? "up" : "down"}`}>{fmtPct(delta)}</div>
    )}
  </div>
);

const MoveCard = ({ c }) => (
  <Link
    to={`/asset/crypto/${c.id}`}
    data-testid={`mover-${c.id}`}
    className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/15 transition-all"
  >
    <div className="flex items-center gap-3 min-w-0 flex-1">
      <img src={c.image} alt={c.name} className="w-7 h-7 rounded-full flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{c.name}</div>
        <div className="text-[10px] uppercase text-neutral-500">{c.symbol}</div>
      </div>
    </div>
    {c.sparkline_in_7d?.price?.length > 1 && (
      <Sparkline data={c.sparkline_in_7d.price.slice(-30)} width={64} height={24} />
    )}
    <div className="text-right whitespace-nowrap">
      <div className="font-mono-num text-sm">{fmtUsd(c.current_price)}</div>
      <div className={`text-xs font-mono-num ${c.price_change_percentage_24h >= 0 ? "up" : "down"}`}>
        {fmtPct(c.price_change_percentage_24h)}
      </div>
    </div>
  </Link>
);

const Crypto = () => {
  const [global, setGlobal] = useState(null);
  const [trending, setTrending] = useState([]);
  const [gainers, setGainers] = useState([]);
  const [losers, setLosers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [coins, setCoins] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    Promise.allSettled([
      client.get("/crypto/global"),
      client.get("/crypto/trending"),
      client.get("/crypto/gainers-losers"),
      client.get("/crypto/categories"),
    ]).then((res) => {
      if (res[0].status === "fulfilled") setGlobal(res[0].value.data);
      if (res[1].status === "fulfilled") setTrending(res[1].value.data.trending || []);
      if (res[2].status === "fulfilled") {
        setGainers(res[2].value.data.gainers || []);
        setLosers(res[2].value.data.losers || []);
      }
      if (res[3].status === "fulfilled") setCategories(res[3].value.data.categories || []);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    client.get(`/crypto/markets?per_page=30&page=${page}`).then(({ data }) => {
      setCoins(data.results || []);
      setLoading(false);
    });
  }, [page]);

  const filteredCoins = query
    ? coins.filter((c) =>
        (c.name + " " + c.symbol).toLowerCase().includes(query.toLowerCase())
      )
    : coins;

  return (
    <div className="space-y-10 fade-in-up" data-testid="crypto-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <span className="chip">
            <Bitcoin size={11} /> Crypto
          </span>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-3">Crypto Universe</h1>
          <p className="text-neutral-400 mt-2 text-sm">
            {global ? `${fmtNum(global.active_cryptocurrencies)} active assets · ${fmtNum(global.markets)} markets · live data from CoinGecko` : "Loading…"}
          </p>
        </div>
      </div>

      {/* Global stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile
          label="Total Market Cap"
          value={global ? fmtUsd(global.total_market_cap_usd, { compact: true, digits: 2 }) : "—"}
          delta={global?.market_cap_change_percentage_24h_usd}
        />
        <StatTile
          label="24h Volume"
          value={global ? fmtUsd(global.total_volume_usd, { compact: true, digits: 2 }) : "—"}
        />
        <StatTile
          label="BTC Dominance"
          value={global ? `${(global.market_cap_percentage?.btc || 0).toFixed(2)}%` : "—"}
        />
        <StatTile
          label="ETH Dominance"
          value={global ? `${(global.market_cap_percentage?.eth || 0).toFixed(2)}%` : "—"}
        />
      </div>

      {/* Gainers / Losers / Trending */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="glass p-6" data-testid="gainers-panel">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={14} className="text-emerald-400" />
            <div className="text-xs uppercase tracking-widest text-neutral-500">Top Gainers (24h)</div>
          </div>
          <div className="space-y-2">
            {gainers.slice(0, 8).map((c) => <MoveCard key={c.id} c={c} />)}
            {gainers.length === 0 && <div className="text-neutral-500 text-sm">Loading…</div>}
          </div>
        </div>
        <div className="glass p-6" data-testid="losers-panel">
          <div className="flex items-center gap-2 mb-4">
            <TrendingDown size={14} className="text-rose-400" />
            <div className="text-xs uppercase tracking-widest text-neutral-500">Top Losers (24h)</div>
          </div>
          <div className="space-y-2">
            {losers.slice(0, 8).map((c) => <MoveCard key={c.id} c={c} />)}
            {losers.length === 0 && <div className="text-neutral-500 text-sm">Loading…</div>}
          </div>
        </div>
        <div className="glass p-6" data-testid="trending-panel">
          <div className="flex items-center gap-2 mb-4">
            <Flame size={14} className="text-orange-400" />
            <div className="text-xs uppercase tracking-widest text-neutral-500">Trending Now</div>
          </div>
          <div className="space-y-2">
            {trending.slice(0, 8).map((t) => (
              <Link
                key={t.id}
                to={`/asset/crypto/${t.id}`}
                data-testid={`trend-${t.id}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/15"
              >
                <img src={t.thumb} alt="" className="w-7 h-7 rounded-full" />
                <div className="flex-1 truncate">
                  <div className="text-sm font-medium truncate">{t.name}</div>
                  <div className="text-[10px] uppercase text-neutral-500">{t.symbol}</div>
                </div>
                {t.market_cap_rank && (
                  <div className="text-xs text-neutral-500 font-mono-num">#{t.market_cap_rank}</div>
                )}
              </Link>
            ))}
            {trending.length === 0 && <div className="text-neutral-500 text-sm">Loading…</div>}
          </div>
        </div>
      </div>

      {/* Categories */}
      {categories.length > 0 && (
        <section data-testid="categories-section">
          <div className="flex items-center gap-2 mb-4">
            <Coins size={14} className="text-blue-400" />
            <div className="text-xs uppercase tracking-widest text-neutral-500">Sectors / Categories</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {categories.slice(0, 8).map((cat) => (
              <div key={cat.id || cat.name} data-testid={`category-${cat.id}`} className="glass glass-hover p-5">
                <div className="font-display text-sm leading-snug">{cat.name}</div>
                <div className="font-mono-num text-lg mt-2">{fmtUsd(cat.market_cap, { compact: true, digits: 1 })}</div>
                <div className={`text-xs font-mono-num ${(cat.market_cap_change_24h || 0) >= 0 ? "up" : "down"}`}>
                  {fmtPct(cat.market_cap_change_24h)}
                </div>
                <div className="flex -space-x-2 mt-3">
                  {(cat.top_3_coins || []).map((url, i) => (
                    <img key={url || i} src={url} alt="" className="w-6 h-6 rounded-full border-2 border-black bg-black" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Full markets */}
      <section>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">All Markets</div>
            <h2 className="font-display text-2xl mt-1">Live Crypto Prices · Page {page}</h2>
          </div>
          <input
            data-testid="crypto-filter"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter on this page…"
            className="bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm w-full sm:w-64 focus:outline-none focus:border-white/20"
          />
        </div>
        <div className="glass p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-neutral-500 text-xs uppercase tracking-wider">
                  <th className="px-6 py-3">#</th>
                  <th className="px-6 py-3">Asset</th>
                  <th className="px-6 py-3 text-right">Price</th>
                  <th className="px-6 py-3 text-right">24h</th>
                  <th className="px-6 py-3 text-right hidden md:table-cell">7d</th>
                  <th className="px-6 py-3 text-right hidden md:table-cell">Market Cap</th>
                  <th className="px-6 py-3 text-right hidden md:table-cell">Volume 24h</th>
                  <th className="px-6 py-3 hidden lg:table-cell">Last 7d</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="px-6 py-8 text-neutral-500 text-center">Loading…</td></tr>}
                {!loading && filteredCoins.map((c) => (
                  <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                    <td className="px-6 py-4 text-neutral-500 font-mono-num">{c.market_cap_rank}</td>
                    <td className="px-6 py-4">
                      <Link to={`/asset/crypto/${c.id}`} data-testid={`crypto-${c.id}`} className="flex items-center gap-3 hover:underline">
                        <img src={c.image} alt={c.name} className="w-7 h-7 rounded-full" />
                        <div>
                          <div className="font-medium">{c.name}</div>
                          <div className="text-xs text-neutral-500 uppercase">{c.symbol}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-right font-mono-num">{fmtUsd(c.current_price)}</td>
                    <td className={`px-6 py-4 text-right font-mono-num ${c.price_change_percentage_24h >= 0 ? "up" : "down"}`}>{fmtPct(c.price_change_percentage_24h)}</td>
                    <td className={`px-6 py-4 text-right font-mono-num hidden md:table-cell ${c.price_change_percentage_7d_in_currency >= 0 ? "up" : "down"}`}>{fmtPct(c.price_change_percentage_7d_in_currency)}</td>
                    <td className="px-6 py-4 text-right font-mono-num hidden md:table-cell">{fmtUsd(c.market_cap, { compact: true, digits: 1 })}</td>
                    <td className="px-6 py-4 text-right font-mono-num hidden md:table-cell">{fmtUsd(c.total_volume, { compact: true, digits: 1 })}</td>
                    <td className="px-6 py-4 hidden lg:table-cell">
                      {c.sparkline_in_7d?.price && <Sparkline data={c.sparkline_in_7d.price} width={120} height={32} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-4 flex items-center justify-between border-t border-white/5">
            <button
              data-testid="crypto-prev"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-glass !py-2 !px-4 text-xs disabled:opacity-50"
            >
              ← Prev
            </button>
            <div className="text-xs text-neutral-500">Page {page}</div>
            <button
              data-testid="crypto-next"
              onClick={() => setPage((p) => p + 1)}
              className="btn-glass !py-2 !px-4 text-xs"
            >
              Next →
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Crypto;
